import React, { type ReactNode } from "react";
import { type LinkProps, Flex, Text, type TextProps, type FlexProps, Box } from "@chakra-ui/react";
import { Button } from "ui/button";
import { Link } from "ui/link";
import { cardHoverTranslation } from "utils";

type ResourceCardProps = {
  href: LinkProps["href"];
  target?: LinkProps["target"];
  linkAriaLabel?: string;
  actionLabel?: string;
  eyebrow?: string;
  title?: string;
  titleAs?: TextProps["as"];
  excerpt?: string;
  image?: ReactNode;
  onClick?: () => void;
};

export function ResourceCard({
  image,
  children,
  href,
  target,
  linkAriaLabel,
  eyebrow,
  title,
  titleAs = "h2",
  excerpt,
  actionLabel,
  onClick,
  ...props
}: ResourceCardProps & FlexProps) {
  return (
    /**
     * Parent element is necessary to avoid a layout shift when hovering over the bottom of the card.
     * See `cardHoverTranslation` for more details.
     */
    <Flex className="group" boxSize="100%">
      <Link
        variant="unstyled"
        href={href}
        target={target}
        aria-label={linkAriaLabel ?? title}
        boxSize="100%"
        borderRadius="big"
        pos="relative"
        transition="transform .2s ease"
        // Important: Do not animate box-shadow, it will have a major performance impact. Use a pseudo element instead.
        // Read more here: https://tobiasahlin.com/blog/how-to-animate-box-shadow/
        _after={{
          content: '""',
          position: "absolute",
          borderRadius: "big",
          inset: 0,
          boxShadow: "hoverTranslateY.card",
          opacity: 0,
          transition: "opacity .2s ease",
        }}
        _groupHover={{
          ...cardHoverTranslation,
          _after: {
            opacity: { base: "none", md: 1 },
          },
        }}
        onClick={onClick}
      >
        <Flex
          boxSize="100%"
          bg="white"
          border="1px"
          borderColor="brand.border.extraLight"
          boxShadow="card"
          borderRadius="big"
          overflow="hidden"
          pos="relative"
          flexDirection="column"
          alignItems="flex-start"
          {...props}
        >
          {image}
          <Flex
            align="start"
            direction="column"
            flex={1}
            gap={3}
            width="100%"
            p="small"
            pb="big"
            borderTop={image ? "1px solid" : "none"}
            borderColor={{ base: "transparent", md: "brand.border.extraLight" }}
            minH={image ? { base: "auto", md: "200px" } : "auto"}
          >
            {eyebrow && (
              <Text as="span" textStyle="overline1" fontWeight="bold" color="brand.textSecondary.light">
                {eyebrow}
              </Text>
            )}

            {title && (
              <Text as={titleAs} textStyle="headline3">
                {title}
              </Text>
            )}

            {excerpt && (
              <Text textStyle="caption1" color="brand.textSecondary.light" mb={1}>
                {excerpt}
              </Text>
            )}

            {children}

            {actionLabel && (
              <Box pt="tiny" mt="auto">
                <Button hasRightChevron as="span" size="sm">
                  {actionLabel}
                </Button>
              </Box>
            )}
          </Flex>
        </Flex>
      </Link>
    </Flex>
  );
}
